import Avatar, { AvatarProps } from '@mui/material/Avatar';
import translate from '@translate';
import moment from 'moment';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { ConstantsCodesObjectType } from 'src/types/constantsCodes';
import { isDefined } from 'src/utils/isDefined';
import Steps, { StepType } from '../Steps';
import Typography from '../Typography';
import { formatDataCodes, formatDataCallback, getIcon, hasVisibleHistoryRows, isPresentHistoryValue, transalteKey } from './functions';
import {
    ChangelogDataValueType,
    ChangelogUpdateType,
    ChangelogValuesType,
    HistoryFormatedDataType,
    HistoryUpdatesItemsType,
    HistoryUpdatesItemType,
    TransalteRowType,
    KeyCallbackType
} from './types';

import { styled } from '@mui/material/styles';
import { colors, sizes } from 'src/themes/default';

const SEPARATOR_PART = '          -';
const SEPARATOR_RECURSIVE = '      ';

const HistoryContainer = styled('div')(({ theme }) => ({
    '& .MuiStepContent-root': {
        marginLeft: '15px'
    },
    '& .MuiStepConnector-vertical': {
        marginLeft: '15px'
    }
}));

const StyledAvatar = styled(Avatar)<AvatarProps>(({ theme }) => ({
    color: colors.primary_O,
    backgroundColor: colors.primary_W,
    border: '1px solid',
    borderColor: colors.primary_O,
    width: theme.spacing(4),
    height: theme.spacing(4)
}));

const StyledLabel = styled('div')(({ theme }) => ({
    color: '#000',
    position: 'relative',
    cursor: 'pointer',

    '& .MuiTypography-subtitle2': {
        fontWeight: sizes.bold_W
    },

    '& .historyLabelUser': {
        position: 'absolute',
        top: '0px',
        right: theme.spacing(2)
    }
}));

export type CustomIconFceType = (row: HistoryFormatedDataType) => ReactNode | null;

export interface HistoryPropsType {
    updates?: ChangelogUpdateType[] | null;
    keyCodes: { [key: string]: ConstantsCodesObjectType };
    updateKeys: HistoryUpdatesItemType[];
    updateItemsKeys?: { [key: string]: HistoryUpdatesItemsType };
    keyItemsCodes?: { [key: string]: { [key: string]: ConstantsCodesObjectType } };
    keyCallbacks?: { [key: string]: KeyCallbackType };
    keyItemsCallbacks?: { [key: string]: { [key: string]: KeyCallbackType } };
    translatePrefix: string;
    customIconFce?: CustomIconFceType;
    /** Keys never shown for CREATE changelog entries (still shown on EDIT). */
    createHiddenKeys?: string[];
    /** Optional metadata rendered under the step title (e.g. link to related entity). */
    stepMeta?: (item: HistoryFormatedDataType) => ReactNode;
    /** Expand all timeline steps by default (useful when changes should be visible immediately). */
    defaultExpandAll?: boolean;
}

const History: FC<HistoryPropsType> = (props) => {
    const [formatData, setFormatData] = useState<HistoryFormatedDataType[]>([]);
    const [expandedSteps, setExpandedSteps] = useState<number[]>([]);

    useEffect(() => {
        if (props.updates) {
            const newFormatData = formatHistory(props.updates);
            const reversed = newFormatData.reverse();
            setFormatData(reversed);
            setExpandedSteps(props.defaultExpandAll ? reversed.map((_, index) => index) : []);
        }
    }, [props.updates, props.defaultExpandAll]);

    const formatHistory = (data: ChangelogUpdateType[]): HistoryFormatedDataType[] => {
        return data
            .map((item) => {
                const newData: ChangelogValuesType = JSON.parse(JSON.stringify(item.data) || '{}');
                Object.keys(props.keyCodes).forEach((key) => {
                    if (newData && isPresentHistoryValue(newData[key]) && !Array.isArray(newData[key])) {
                        // @ts-ignore
                        newData[key] = formatDataCodes(newData[key], props.keyCodes[key]);
                    }
                });

                if (props.keyCallbacks) {
                    Object.keys(props.keyCallbacks).forEach((key) => {
                        if (newData && isPresentHistoryValue(newData[key]) && !Array.isArray(newData[key])) {
                            // @ts-ignore
                            newData[key] = formatDataCallback(newData[key], props.keyCallbacks[key]);
                        }
                    });
                }

                const isCreate = item.type === 'create';
                return { ...item, data: newData, translateRows: translateHistory(newData, isCreate) };
            })
            .filter((item) => hasVisibleHistoryRows(item.translateRows));
    };

    const translateHistory = (data: ChangelogValuesType, isCreate: boolean): TransalteRowType[] => {
        const hideEmpty = isCreate;
        const createHiddenKeys = isCreate ? props.createHiddenKeys ?? [] : [];
        let rows: TransalteRowType[] = props.updateKeys
            .filter((item) => !createHiddenKeys.includes(item.key))
            .map((item) =>
                transalteKey(data, item.key, item.translateKey, item.format, { hideEmpty })
            )
            .filter((item) => item !== null);
        if (!data) {
            return [];
        }
        if (isDefined(data.errorData)) rows.push(String(data.errorData));

        const itemsRows = getItemsHistory(data, isCreate);
        rows.push(...itemsRows);

        return rows;
    };

    const getItemsHistory = (data: ChangelogValuesType, isCreate: boolean): string[] => {
        let rows: string[] = [];
        if (!props.updateItemsKeys) {
            return rows;
        }
        const hideEmpty = isCreate;
        const translateOptions = { hideEmpty };

        Object.keys(props.updateItemsKeys).forEach((customKey) => {
            if (
                !!data[customKey] &&
                data[customKey] !== null &&
                Array.isArray(data[customKey]) &&
                props.updateItemsKeys
            ) {
                const customItem = props.updateItemsKeys[customKey];
                const customCodes = (props.keyItemsCodes && props.keyItemsCodes[customKey]) || {};
                const customCallbacks = (props.keyItemsCallbacks && props.keyItemsCallbacks[customKey]) || {};
                const updateItems = data[customKey];

                if (!Array.isArray(updateItems)) {
                    return;
                }
                updateItems.forEach((updateItem) => {
                    if (!updateItem) {
                        return;
                    }
                    if (typeof updateItem !== 'object') {
                        return;
                    }

                    const contentRows: string[] = [];

                    if (
                        'changes' in updateItem &&
                        typeof updateItem.changes === 'object' &&
                        !updateItem.changes?.new &&
                        !updateItem.changes?.old &&
                        updateItem.changes
                    ) {
                        // add required items
                        if (customItem.requiredItems) {
                            customItem.requiredItems.forEach((reqItemKey) => {
                                const row = transalteKey(
                                    // @ts-ignore
                                    updateItem,
                                    reqItemKey,
                                    [
                                        SEPARATOR_PART,
                                        `${props.translatePrefix}.history.${reqItemKey}`
                                    ],
                                    undefined,
                                    translateOptions
                                );
                                if (row) {
                                    contentRows.push(row);
                                }
                            });
                        }

                        // add updates
                        Object.keys(updateItem.changes).forEach((updateKey) => {
                            const itemKeysItem = customItem.itemKeys.find((item) => item.key === updateKey && !customItem.requiredItems?.includes(item.key));

                            if (itemKeysItem) {
                                if (
                                    props.updateItemsKeys &&
                                    Object.keys(props.updateItemsKeys).includes(itemKeysItem.key)
                                ) {
                                    contentRows.push(
                                        ...(getItemsHistory(
                                            // @ts-ignore
                                            updateItem.changes,
                                            isCreate
                                        ) || [])
                                            .filter((item) => !!item)
                                            .map((item) => `${SEPARATOR_RECURSIVE}${item}`)
                                    );
                                } else {
                                    const updateData: ChangelogDataValueType = {};
                                    // @ts-ignore
                                    updateData[updateKey] = { ...(updateItem.changes[updateKey] || {}) };
                                    if (customCodes[updateKey]) {
                                        // @ts-ignore
                                        updateData[updateKey] = formatDataCodes(
                                            updateData[updateKey],
                                            customCodes[updateKey]
                                        );
                                    }
                                    if (customCallbacks[updateKey]) {
                                        // @ts-ignore
                                        updateData[updateKey] = formatDataCallback(
                                            updateData[updateKey],
                                            customCallbacks[updateKey]
                                        );
                                    }
                                    const row = transalteKey(
                                        updateData,
                                        itemKeysItem.key,
                                        // @ts-ignore
                                        [SEPARATOR_PART, itemKeysItem.translateKey],
                                        itemKeysItem.format || undefined,
                                        translateOptions
                                    );
                                    if (row) {
                                        contentRows.push(row);
                                    }
                                }
                            }
                        });

                        if (contentRows.length > 0) {
                            rows.push(`${translate(`${props.translatePrefix}.history.${customKey}.edit`)}: {`);
                            rows.push(...contentRows);
                            rows.push('}');
                        }
                    } else {
                        let headerLabel: string;
                        if ('diffDelete' in updateItem) {
                            headerLabel = `${translate(`${props.translatePrefix}.history.${customKey}.delete`)}:{`;
                        } else { // @ts-ignore
                            if (!Object.keys(updateItem).some((key) => updateItem[key] && typeof updateItem[key] === 'object' && Object.keys(updateItem[key]).includes('new'))) {
                                headerLabel = `${translate(`${props.translatePrefix}.history.${customKey}.new`)}:{`;
                            } else {
                                headerLabel = `${translate(`${props.translatePrefix}.history.${customKey}.edit`)}:{`;
                            }
                        }

                        Object.keys(customCodes).forEach((key) => {
                            const itemRecord = updateItem as Record<string, ChangelogDataValueType | undefined>;
                            const fieldValue = itemRecord[key];
                            if (isPresentHistoryValue(fieldValue)) {
                                itemRecord[key] = formatDataCodes(fieldValue, customCodes[key]);
                            }
                        });

                        Object.keys(customCallbacks).forEach((key) => {
                            const itemRecord = updateItem as Record<string, ChangelogDataValueType | undefined>;
                            const fieldValue = itemRecord[key];
                            if (isPresentHistoryValue(fieldValue)) {
                                itemRecord[key] = formatDataCallback(fieldValue, customCallbacks[key]);
                            }
                        });

                        // add required items
                        if (customItem.requiredItems) {
                            customItem.requiredItems.forEach((reqItemKey) => {
                                const row = transalteKey(
                                    // @ts-ignore
                                    updateItem,
                                    reqItemKey,
                                    [
                                        SEPARATOR_PART,
                                        `${props.translatePrefix}.history.${reqItemKey}`
                                    ],
                                    undefined,
                                    translateOptions
                                );
                                if (row) {
                                    contentRows.push(row);
                                }
                            });
                        }

                        // add items
                        customItem.itemKeys.forEach((itemKey) => {
                            if (
                                // @ts-ignore
                                !(itemKey.keyRequiredExclude && updateItem[itemKey.keyRequiredExclude]) && !customItem.requiredItems?.includes(itemKey.key)
                            ) {
                                if (props.updateItemsKeys && Object.keys(props.updateItemsKeys).includes(itemKey.key)) {
                                    contentRows.push(
                                        ...(getItemsHistory(
                                            // @ts-ignore
                                            updateItem,
                                            isCreate
                                        ) || [])
                                            .filter((item) => !!item)
                                            .map((item) => `${SEPARATOR_RECURSIVE}${item}`)
                                    );
                                } else {
                                    const row = transalteKey(
                                        // @ts-ignore
                                        updateItem,
                                        itemKey.key,
                                        [SEPARATOR_PART, itemKey.translateKey],
                                        itemKey.format,
                                        translateOptions
                                    );
                                    if (row) {
                                        contentRows.push(row);
                                    }
                                }
                            }
                        });

                        if (contentRows.length > 0) {
                            rows.push(headerLabel);
                            rows.push(...contentRows);
                            rows.push('}');
                        }
                    }
                });
            }
        });

        return rows;
    };

    const handleActive = (index: number) => {
        setExpandedSteps((prev) =>
            prev.includes(index) ? prev.filter((stepIndex) => stepIndex !== index) : [...prev, index]
        );
    };

    const createStepsData = (): StepType[] => {
        return formatData.map(
            (item, key): StepType => {
                const date = item.created_at && moment(item.created_at);
                const userName =
                    item.user && typeof item.user === 'object' ? `${item.user.NAME} ${item.user.SURNAME}` : item.user;
                const message = item.message
                    ? translate(`${props.translatePrefix}.history.${item.message}`, item.data)
                    : item.type;

                return {
                    label: (
                        <StyledLabel onClick={() => handleActive(key)}>
                            <Typography variant="subtitle2" display="inline">
                                {message}
                            </Typography>
                            {date && (
                                <Typography variant="caption" display="block">{`${date.format('LLL')}`}</Typography>
                            )}
                            {props.stepMeta?.(item)}
                            {item.user && (
                                <Typography variant="caption" display="inline" className="historyLabelUser">
                                    {userName}
                                </Typography>
                            )}
                        </StyledLabel>
                    ),
                    content: (
                        <>
                            {item.translateRows &&
                                item.translateRows
                                    .filter((text) => text)
                                    .map((text, keyText) => {
                                        return (
                                            <Typography
                                                key={String(keyText)}
                                                variant="caption"
                                                display="block"
                                                sx={{ whiteSpace: 'pre' }}
                                            >
                                                {text}
                                            </Typography>
                                        );
                                    })}
                        </>
                    ),
                    icon: <StyledAvatar className="historyIcon">{getIcon(item, props.customIconFce)}</StyledAvatar>
                };
            }
        );
    };

    return (
        <HistoryContainer>
            <Steps steps={createStepsData()} expandedSteps={expandedSteps} />
        </HistoryContainer>
    );
};

export default History;
